import { useMemo } from 'react'

import { formatDateForInput, parseRenewalDate } from '@/features/admin/renewals/utils'
import {
  useGetMyApplications,
  useGetMyCPDPoints,
} from '@/utils/api/membership-service'
import type { ApplicationResponseDTO } from '@/utils/api/membership-service/models'
import { ApplicationResponseDTOStatus } from '@/utils/api/membership-service/models'
import { useGetCurentUserDetails } from '@/utils/api/user-service'
import { isAdminUser } from '@/lib/role-guards'

export interface DashboardQuickLink {
  id: string
  label: string
  description: string
  href: string
}

export type MembershipStatus = 'active' | 'inactive' | 'disciplined' | 'approved'

export interface CertificateEligibility {
  eligible: boolean
  duesRequirementMet: boolean
  cpdRequirementMet: boolean
  message: string
  ctaHref: string
}

export interface DashboardSummary {
  profile: {
    memberName: string
    membershipNumber: string
    membershipCategory: string
    membershipStatus: MembershipStatus
    membershipValidUntil: string | null
    memberSince: string | null
  }
  applications: {
    total: number
    recent: Array<{
      id: string
      title: string
      status: string
    }>
  }
  cpd: {
    totalPoints: number
    requiredPoints: number
    eventsCompleted: number
    recentEvents: Array<{
      id: string
      title: string
      completedOn: string
      points: number
      certificateAvailable: boolean
    }>
  }
  duesPaidThrough: string | null
  certificateEligibility: CertificateEligibility
  quickLinks: DashboardQuickLink[]
}

const CERTIFICATE_ROUTE = '/applications/certificate-of-practice'
const DEFAULT_REQUIRED_POINTS = 40

function computeCertificateEligibility(
  targetPoints: number,
  earnedPoints: number,
  duesThrough: string | null,
): CertificateEligibility {
  const now = Date.now()
  const duesExpiry = duesThrough ? Date.parse(duesThrough) : Number.NaN
  const duesRequirementMet = Number.isFinite(duesExpiry) ? duesExpiry >= now : false
  const cpdRequirementMet = targetPoints > 0 ? earnedPoints >= targetPoints : false
  const eligible = duesRequirementMet && cpdRequirementMet

  const outstandingPoints = Math.max(targetPoints - earnedPoints, 0)

  let message = 'We could not determine your certificate eligibility yet. Please try again later.'

  if (eligible) {
    message = 'You meet the dues and CPD requirements to request a Certificate of Practice.'
  } else if (!duesRequirementMet && Number.isFinite(duesExpiry)) {
    message = 'Renew your membership dues to remain in good standing and request a certificate.'
  } else if (!duesRequirementMet && !Number.isFinite(duesExpiry)) {
    message = 'We could not confirm your dues status. Please refresh or contact support.'
  } else if (!cpdRequirementMet) {
    message = `Earn ${outstandingPoints} more CPD point${outstandingPoints === 1 ? '' : 's'} to request a Certificate of Practice.`
  }

  return {
    eligible,
    duesRequirementMet,
    cpdRequirementMet,
    message,
    ctaHref: CERTIFICATE_ROUTE,
  }
}

function normaliseMembershipStatus(rawStatus: ApplicationResponseDTO['status']): MembershipStatus {
  if (!rawStatus) {
    return 'inactive'
  }

  const normalised = rawStatus.toString().toLowerCase()
  if (normalised === ApplicationResponseDTOStatus.APPROVED.toLowerCase()) {
    return 'approved'
  }
  if (normalised === 'active') {
    return 'active'
  }
  if (normalised === 'disciplined') {
    return 'disciplined'
  }

  return 'inactive'
}

function buildQuickLinks(isAdmin: boolean): DashboardQuickLink[] {
  const links: DashboardQuickLink[] = [
    {
      id: 'cpd-bank',
      label: 'CPD Bank',
      description: 'View and manage your CPD points.',
      href: '/benefits/cpd-bank',
    },
    {
      id: 'prospectus',
      label: 'Prospectus',
      description: 'Explore our programs and offerings.',
      href: '/prospectus',
    },
    {
      id: 'calendar',
      label: 'Calendar',
      description: 'Check upcoming events and deadlines.',
      href: '/benefits/calendar',
    },
  ]

  if (isAdmin) {
    links.push({
      id: 'insights',
      label: 'Insights',
      description: 'Review resource usage analytics.',
      href: '/admin/insights',
    })
  }

  return links
}

export interface DashboardSummaryQueryResult {
  data: DashboardSummary | undefined
  isPending: boolean
  isFetching: boolean
  isError: boolean
  error: Error | null
  refetch: () => Promise<void>
}

export function useDashboardSummaryQuery(): DashboardSummaryQueryResult {
  const userQuery = useGetCurentUserDetails({
    query: {
      select: (response) => response.data,
    },
  })

  const applicationsQuery = useGetMyApplications(
    { size: 5 },
    {
      query: {
        select: (response) => response.data,
      },
    },
  )

  const cpdPointsQuery = useGetMyCPDPoints({
    query: {
      select: (response) => response.data,
    },
  })

  const isPending = userQuery.isPending || applicationsQuery.isPending || cpdPointsQuery.isPending
  const isFetching = userQuery.isFetching || applicationsQuery.isFetching || cpdPointsQuery.isFetching
  const isError = Boolean(userQuery.isError || applicationsQuery.isError || cpdPointsQuery.isError)
  const error =
    (userQuery.error ?? applicationsQuery.error ?? cpdPointsQuery.error ?? null) as Error | null

  const data = useMemo<DashboardSummary | undefined>(() => {
    if (!userQuery.data || !applicationsQuery.data || !cpdPointsQuery.data) {
      return undefined
    }

    const recentApplications = applicationsQuery.data.content ?? []
    const recentApplication = recentApplications.find((app) => {
      if (!app) return false
      const expiryDate = parseRenewalDate(app.membershipExpiryAt ?? '')
      if (!expiryDate) return false
      const now = new Date()
      return expiryDate.getTime() >= now.getTime()
    }) ?? recentApplications[0]

    const currentUser = userQuery.data
    const memberName = currentUser.name ?? 'Unknown Member'
    const membershipNumber = recentApplication?.membershipNumber ?? `MEM-${(recentApplication?.id ?? 1000) + 10}`
    const membershipCategory = recentApplication?.categoryName ?? 'Uncategorized'
    const membershipStatus = normaliseMembershipStatus(recentApplication?.status)
    const membershipValidUntil = formatDateForInput(recentApplication?.membershipExpiryAt ?? null)
    const memberSince = formatDateForInput(recentApplication?.createdAt ?? null)

    const applications = {
      total: applicationsQuery.data.totalElements ?? 0,
      recent: recentApplications.map((app) => ({
        id: app.id?.toString() ?? '',
        title: app.categoryName ?? 'Untitled Application',
        status: app.status?.toString() ?? 'Unknown',
      })),
    }

    const cpdPayload = cpdPointsQuery.data
    const totalPoints = cpdPayload.totalPoints ?? 0
    const requiredPoints = cpdPayload.requiredPoints ?? DEFAULT_REQUIRED_POINTS
    const eventsCompleted = cpdPayload.eventsCompleted ?? 0

    const duesPaidThrough = membershipValidUntil

    const certificateEligibility = computeCertificateEligibility(
      requiredPoints,
      totalPoints,
      duesPaidThrough,
    )

    return {
      profile: {
        memberName,
        membershipNumber,
        membershipCategory,
        membershipStatus,
        membershipValidUntil,
        memberSince,
      },
      applications,
      cpd: {
        totalPoints,
        requiredPoints,
        eventsCompleted,
        recentEvents: [],
      },
      duesPaidThrough,
      certificateEligibility,
      quickLinks: buildQuickLinks(isAdminUser(currentUser)),
    }
  }, [applicationsQuery.data, cpdPointsQuery.data, userQuery.data])

  const refetch = async () => {
    await Promise.all([userQuery.refetch(), applicationsQuery.refetch(), cpdPointsQuery.refetch()])
  }

  return {
    data,
    isPending,
    isFetching,
    isError,
    error,
    refetch,
  }
}
